/**
 * AutoDrive Theme — Variant Picker JS
 *
 * Reads product variants from a JSON script tag in the page,
 * then reacts to option changes to:
 *  - Find the matching variant
 *  - Update price display
 *  - Update the main product image
 *  - Update the URL (history.pushState)
 *  - Update add-to-cart button availability
 *  - Disable unavailable option combinations
 */

(function () {
  'use strict';

  function VariantPicker(container) {
    this.container   = container;
    this.productForm = container.querySelector('[data-product-form]');
    this.optionEls   = Array.from(container.querySelectorAll('[data-option-selector]'));
    this.variantIdInput = container.querySelector('[name="id"]');

    // Parse variants from embedded JSON
    var variantsScript = container.querySelector('[data-product-variants-json]');
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

    if (!this.variants.length || !this.variantIdInput) return;

    this._bindEvents();
    this._updateState();
  }

  VariantPicker.prototype._bindEvents = function () {
    var self = this;
    this.optionEls.forEach(function (el) {
      el.addEventListener('change', function () {
        self._updateState();
      });
    });
  };

  VariantPicker.prototype._getSelectedOptions = function () {
    return this.optionEls.map(function (el) {
      if (el.type === 'radio' || el.type === 'checkbox') {
        // Find checked radio in same group
        var checked = el.closest('[data-option-group]') &&
                      el.closest('[data-option-group]').querySelector('input:checked');
        return checked ? checked.value : null;
      }
      return el.value;
    });
  };

  VariantPicker.prototype._findVariant = function (options) {
    return this.variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return opt === options[i];
      });
    });
  };

  VariantPicker.prototype._updateState = function () {
    var selectedOptions = this._getSelectedOptions();
    var variant;

    // No option selectors = single default variant, just use the first one
    if (this.optionEls.length === 0) {
      variant = this.variants[0] || null;
    } else {
      variant = this._findVariant(selectedOptions);
    }

    this._updateVariantId(variant);
    this._updatePrice(variant);
    this._updateImage(variant);
    this._updateButton(variant);
    this._updateURL(variant);
    this._markUnavailable(selectedOptions);
  };

  VariantPicker.prototype._updateVariantId = function (variant) {
    if (!this.variantIdInput) return;
    this.variantIdInput.value = variant ? variant.id : '';
  };

  VariantPicker.prototype._updatePrice = function (variant) {
    var priceEl      = this.container.querySelector('[data-variant-price]');
    var compareEl    = this.container.querySelector('[data-variant-compare-price]');
    var savingsEl    = this.container.querySelector('[data-variant-savings]');
    var savingsPct   = this.container.querySelector('[data-variant-savings-pct]');

    if (!variant) return;

    if (priceEl) {
      priceEl.textContent = _formatMoney(variant.price);
      priceEl.classList.toggle('price--sale', variant.compare_at_price > variant.price);
    }

    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = _formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    if (savingsEl && savingsPct) {
      if (variant.compare_at_price > variant.price) {
        var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
        savingsPct.textContent = pct + '% off';
        savingsEl.style.display = '';
      } else {
        savingsEl.style.display = 'none';
      }
    }
  };

  VariantPicker.prototype._updateImage = function (variant) {
    if (!variant || !variant.featured_image) return;

    var mainImg   = document.querySelector('[data-product-main-img]');
    var thumbs    = Array.from(document.querySelectorAll('[data-product-thumb]'));

    if (mainImg) {
      mainImg.src = variant.featured_image.src;
      if (variant.featured_image.alt) mainImg.alt = variant.featured_image.alt;
    }

    // Activate matching thumbnail
    thumbs.forEach(function (thumb) {
      var matches = thumb.dataset.src && thumb.dataset.src.includes(
        variant.featured_image.src.split('?')[0].split('/').pop().split('.')[0]
      );
      var wrapper = thumb.closest('.product-media__thumb');
      if (wrapper) wrapper.classList.toggle('is-active', !!matches);
    });
  };

  VariantPicker.prototype._updateButton = function (variant) {
    var btn = this.container.querySelector('[data-add-to-cart-btn]');
    var btnText = this.container.querySelector('[data-add-to-cart-text]');
    if (!btn) return;

    if (!variant) {
      btn.disabled = true;
      if (btnText) btnText.textContent = 'Unavailable';
      return;
    }

    if (!variant.available) {
      btn.disabled = true;
      if (btnText) btnText.textContent = 'Sold Out';
    } else {
      btn.disabled = false;
      if (btnText) btnText.textContent = 'Add to Cart';
    }
  };

  VariantPicker.prototype._updateURL = function (variant) {
    if (!variant || !window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variantId: variant.id }, '', url.toString());
  };

  /**
   * Mark option values unavailable when no in-stock variant matches.
   */
  VariantPicker.prototype._markUnavailable = function (selectedOptions) {
    var self = this;

    this.optionEls.forEach(function (el, positionIndex) {
      if (el.type !== 'radio' && el.tagName !== 'SELECT') return;

      var siblings = el.closest('[data-option-group]') &&
                     Array.from(el.closest('[data-option-group]').querySelectorAll('input[type="radio"]'));
      if (!siblings) return;

      siblings.forEach(function (sib) {
        var testOptions = selectedOptions.slice();
        testOptions[positionIndex] = sib.value;

        var available = self.variants.some(function (v) {
          return v.available && v.options.every(function (opt, i) {
            return opt === testOptions[i];
          });
        });

        sib.disabled = !available;
        var box = sib.nextElementSibling;
        if (box) box.style.opacity = available ? '' : '0.35';
      });
    });
  };

  /* ============================================================
     HELPERS
     ============================================================ */
  function _formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ============================================================
     INIT all VariantPicker instances on page
     ============================================================ */
  document.addEventListener('DOMContentLoaded', function () {
    Array.from(document.querySelectorAll('[data-variant-picker]')).forEach(function (el) {
      new VariantPicker(el);
    });
  });

})();
